import React, { createContext, useContext, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { isReadOnlyUser } from '../../utils/rbac';
import Icon from '../AppIcon';

const ReadOnlyContext = createContext({ isReadOnly: false });

export const useReadOnly = () => useContext(ReadOnlyContext);

/**
 * ReadOnlyProvider — wraps the app and provides isReadOnly context.
 * Read-only users see a persistent banner and all write actions are blocked.
 */
export const ReadOnlyProvider = ({ children }) => {
  const { userProfile } = useAuth();
  const isReadOnly = isReadOnlyUser(userProfile);

  return (
    <ReadOnlyContext.Provider value={{ isReadOnly }}>
      {isReadOnly && (
        <div className="fixed top-16 left-0 right-0 z-[999] bg-green-600 text-white text-xs font-medium py-1.5 px-4 flex items-center justify-center gap-2 shadow-md">
          <Icon name="EyeOff" size={13} color="white" />
          <span>Read-Only Mode — You can view data and clock in/out, but cannot make changes.</span>
        </div>
      )}
      {children}
    </ReadOnlyContext.Provider>
  );
};

/**
 * ReadOnlyGuard — wraps any write action (button, form, etc.).
 * If the user is read-only, the action is disabled and a tooltip is shown.
 * Clock-in/out actions are exempt (pass allowClockInOut={true}).
 */
const ReadOnlyGuard = ({ children, allowClockInOut = false, className = '' }) => {
  const { isReadOnly } = useReadOnly();

  if (!isReadOnly) return children;
  if (allowClockInOut) return children;

  return (
    <div className={`relative group ${className}`} title="Read-Only: You cannot make changes with this license.">
      <div className="pointer-events-none opacity-40 select-none">
        {children}
      </div>
      <div className="absolute inset-0 cursor-not-allowed" />
    </div>
  );
};

export default ReadOnlyGuard;
